// components/editor/anteditor/antBlocks.js
export function createAntBlocks(root, onChange = () => {}) {
  const doc = root.ownerDocument;
  const win = doc.defaultView;
  let savedRange = null;
  let enteringItem = null;
  let composing = false;
  let busy = false;
  const element = (node) => node?.nodeType === 3 ? node.parentElement : node;
  const closest = (node, selector) => {
    const found = element(node)?.closest?.(selector);
    return found && found !== root && root.contains(found) ? found : null;
  };
  const own = (li, selector) => [...li.querySelectorAll(selector)].filter((el) => el.closest('li') === li);
  const kind = (list) => list.classList.contains('x-todo') ? 'todo' : list.tagName.toLowerCase();
  const editable = () => root.isContentEditable;

  function remember() {
    const selection = win.getSelection();
    if (selection.rangeCount && root.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      savedRange = selection.getRangeAt(0).cloneRange();
    }
  }

  function range() {
    root.focus({ preventScroll: true });
    const selection = win.getSelection();
    if (savedRange && root.contains(savedRange.commonAncestorContainer)) {
      selection.removeAllRanges();
      selection.addRange(savedRange);
    }
    if (!selection.rangeCount || !root.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      const next = doc.createRange();
      next.selectNodeContents(root);
      next.collapse(false);
      selection.removeAllRanges();
      selection.addRange(next);
    }
    return selection.getRangeAt(0);
  }

  function caret(node, atEnd = false) {
    const next = doc.createRange();
    next.selectNodeContents(node);
    next.collapse(!atEnd);
    win.getSelection().removeAllRanges();
    win.getSelection().addRange(next);
    remember();
  }

  function stripBox(li) {
    own(li, '.x-todo-box, input[type="checkbox"]').forEach((box) => {
      if (box.matches('input')) box.remove();
      else {
        box.querySelectorAll('input[type="checkbox"]').forEach((input) => input.remove());
        box.replaceWith(...box.childNodes);
      }
    });
    li.classList.remove('checklist-item');
    li.style.removeProperty('list-style');
    li.style.removeProperty('list-style-type');
  }

  function decorate(li, reset = false) {
    const inputs = own(li, 'input[type="checkbox"]');
    const checked = !reset && inputs.some((input) => input.checked);
    let wrapper = li.firstElementChild;
    if (!wrapper?.classList.contains('x-todo-box') || inputs.length !== 1 || wrapper.children.length !== 1) {
      stripBox(li);
      wrapper = doc.createElement('span');
      wrapper.className = 'x-todo-box';
      wrapper.innerHTML = '<input type="checkbox" aria-label="Completed">';
      li.prepend(wrapper);
    }
    wrapper.contentEditable = 'false';
    const input = wrapper.querySelector('input');
    input.checked = checked;
    input.toggleAttribute('checked', checked);
    input.setAttribute('aria-label', 'Completed');
    li.classList.add('checklist-item');
    if (wrapper === li.lastChild) li.append(doc.createElement('br'));
  }

  function normalize(resetItem) {
    // Older browser indentation can leave ol/ul directly inside another list.
    root.querySelectorAll('ol > ol, ol > ul, ul > ol, ul > ul').forEach((list) => {
      const previous = list.previousElementSibling;
      if (previous?.tagName === 'LI') previous.append(list);
    });
    // Only direct list children inherit checklist semantics; nested ul/ol keep theirs.
    root.querySelectorAll('ol, ul').forEach((list) => {
      const todo = kind(list) === 'todo';
      list.style.removeProperty('list-style');
      list.style.removeProperty('list-style-type');
      [...list.children].filter((li) => li.tagName === 'LI').forEach((li) => {
        if (todo) decorate(li, li === resetItem);
        else if (li.classList.contains('checklist-item') || own(li, '.x-todo-box').length) stripBox(li);
      });
    });
    root.querySelectorAll('.x-todo:not(ol):not(ul)').forEach((el) => el.classList.remove('x-todo'));
    // Redo may restore a caret bookmark embedded in an insertion transaction.
    root.querySelectorAll('[data-ant-caret]').forEach((marker) => {
      const next = doc.createRange();
      next.setStartBefore(marker);
      next.collapse(true);
      marker.remove();
      win.getSelection().removeAllRanges();
      win.getSelection().addRange(next);
    });
    const selection = win.getSelection();
    const li = closest(selection.anchorNode, 'li');
    const box = li?.querySelector(':scope > .x-todo-box');
    if (selection.isCollapsed && box &&
        ((selection.anchorNode === li && selection.anchorOffset === 0) || box.contains(selection.anchorNode))) {
      const next = doc.createRange();
      next.setStartAfter(box);
      next.collapse(true);
      selection.removeAllRanges();
      selection.addRange(next);
    }
    remember();
  }

  const bookmark = () => '<span data-ant-caret="true">&#8203;</span>';
  function replace(node, html) {
    // Replace only this block. Native insertHTML can merge adjacent ol/ul or
    // discard their type, which is unsafe when checklists share a document.
    busy = true;
    try {
      const fragment = doc.createRange().createContextualFragment(html);
      node.replaceWith(fragment);
      normalize();
    } finally { busy = false; }
    onChange();
  }

  function contentOnly(li) {
    const copy = li.cloneNode(true);
    stripBox(copy);
    return copy;
  }

  function empty(node) {
    const copy = node.cloneNode(true);
    copy.querySelectorAll('.x-todo-box, input[type="checkbox"]').forEach((el) => el.remove());
    return !copy.textContent.replace(/[\s\u200b\u00a0]/g, '') && !copy.querySelector('img, video, iframe, table, ul, ol');
  }

  function exitItem(li) {
    if (li.parentElement.parentElement.tagName === 'LI') { indent(true); return; }
    // Empty native list items know how to exit/outdent; the checkbox must not
    // count as content when the browser makes that decision.
    busy = true;
    try {
      stripBox(li);
      li.innerHTML = '<br>';
      caret(li);
      doc.execCommand('insertParagraph');
      normalize();
    } finally { busy = false; }
    onChange();
  }

  function toggleList(type) {
    if (!editable()) return;
    const selected = range();
    const li = closest(selected.startContainer, 'li');
    if (li) {
      const list = li.parentElement;
      const copy = list.cloneNode(true);
      const items = [...copy.children].filter((item) => item.tagName === 'LI');
      items.forEach(stripBox);
      if (kind(list) === type) {
        replace(list, items.map((item, index) => `<div>${index ? '' : bookmark()}${item.innerHTML || '<br>'}</div>`).join(''));
      } else {
        const next = doc.createElement(type === 'ul' ? 'ul' : 'ol');
        if (type === 'todo') next.className = 'x-todo';
        items.forEach((item) => {
          next.append(item);
          if (type === 'todo') decorate(item);
        });
        const active = items[[...list.children].indexOf(li)] || items[0];
        const box = active.querySelector(':scope > .x-todo-box');
        if (box) box.insertAdjacentHTML('afterend', bookmark());
        else active.insertAdjacentHTML('afterbegin', bookmark());
        replace(list, next.outerHTML);
      }
      return;
    }
    // Native formatBlock safely wraps bare root/cell text without replacing the editor.
    let block = closest(selected.startContainer, 'p, div, h1, h2, h3, h4, h5, h6');
    if (!block || block.querySelector('ol, ul, table, details')) {
      doc.execCommand('formatBlock', false, 'p');
      block = closest(win.getSelection().anchorNode, 'p, div');
    }
    if (!block || block.querySelector('ol, ul, table, details')) return;
    const list = doc.createElement(type === 'ul' ? 'ul' : 'ol');
    if (type === 'todo') list.className = 'x-todo';
    const item = doc.createElement('li');
    item.innerHTML = block.innerHTML || '<br>';
    list.append(item);
    if (type === 'todo') decorate(item);
    const box = item.querySelector('.x-todo-box');
    if (box) box.insertAdjacentHTML('afterend', bookmark());
    else item.insertAdjacentHTML('afterbegin', bookmark());
    replace(block, list.outerHTML);
  }

  function insertDetails() {
    if (!editable()) return;
    range();
    busy = true;
    try {
      doc.execCommand('insertHTML', false, '<details open><summary>' + bookmark() +
        'Summary</summary><p><br></p></details><p><br></p>');
      normalize();
    } finally { busy = false; }
    onChange();
  }

  function indent(outdent = false) {
    if (!editable()) return false;
    const selection = win.getSelection();
    const li = closest(selection.anchorNode, 'li');
    if (!li) return false;
    const node = selection.anchorNode, offset = selection.anchorOffset;
    const list = li.parentElement;
    if (outdent) {
      const parent = list.parentElement;
      if (parent.tagName !== 'LI') {
        const before = list.cloneNode(false), after = list.cloneNode(false);
        let passed = false;
        [...list.children].forEach((item) => {
          if (item === li) passed = true;
          else (passed ? after : before).append(item.cloneNode(true));
        });
        const body = contentOnly(li);
        const tag = body.querySelector('ol, ul') ? 'div' : 'p';
        replace(list, (before.children.length ? before.outerHTML : '') +
          `<${tag}>${bookmark()}${body.innerHTML || '<br>'}</${tag}>` +
          (after.children.length ? after.outerHTML : ''));
        return true;
      }
      parent.after(li);
    } else {
      const previous = li.previousElementSibling;
      if (!previous || previous.tagName !== 'LI') return true;
      let nested = [...previous.children].find((child) => /^(OL|UL)$/.test(child.tagName) && kind(child) === kind(list));
      if (!nested) { nested = list.cloneNode(false); previous.append(nested); }
      nested.append(li);
    }
    if (!list.children.length) list.remove();
    normalize();
    if (root.contains(node)) selection.collapse(node, offset);
    else caret(li);
    remember();
    onChange();
    return true;
  }

  function enter(event) {
    if (!editable() || composing || event.isComposing || !event.cancelable) return;
    const selection = win.getSelection();
    if (!selection.rangeCount) return;
    const current = selection.getRangeAt(0);
    const summary = closest(current.startContainer, 'summary');
    if (summary) {
      event.preventDefault();
      const details = summary.parentElement;
      details.open = true;
      let body = summary.nextElementSibling;
      if (!body) { body = doc.createElement('p'); body.innerHTML = '<br>'; details.append(body); }
      caret(body);
      onChange();
      return;
    }
    const li = closest(current.startContainer, 'li');
    if (li && current.collapsed && empty(li)) {
      event.preventDefault();
      exitItem(li);
    } else {
      enteringItem = li;
      const p = closest(current.startContainer, 'p, div');
      const details = p?.parentElement;
      if (current.collapsed && details?.tagName === 'DETAILS' && p === details.lastElementChild && empty(p)) {
        event.preventDefault();
        const copy = details.cloneNode(true);
        copy.lastElementChild.remove();
        replace(details, copy.outerHTML + '<p>' + bookmark() + '<br></p>');
      }
    }
  }

  function beforeInput(event) {
    if (event.inputType === 'insertParagraph') enter(event);
    if (event.inputType === 'deleteContentBackward') backward(event);
  }
  function backward(event) {
    if (!editable() || composing || event.isComposing || !event.cancelable) return;
    const selection = win.getSelection();
    if (!selection.rangeCount || !selection.isCollapsed) return;
    const current = selection.getRangeAt(0);
    const li = closest(current.startContainer, 'li');
    if (!li || kind(li.parentElement) !== 'todo') return;
    const prefix = current.cloneRange();
    prefix.setStart(li, 0);
    const content = doc.createElement('div');
    content.append(prefix.cloneContents());
    if (!empty(content)) return;
    event.preventDefault();
    const list = li.parentElement;
    if (list.parentElement.tagName === 'LI') {
      indent(true); return;
    }
    const copy = list.cloneNode(true);
    const index = [...list.children].indexOf(li);
    const item = copy.children[index];
    const body = contentOnly(item);
    if (index > 0) {
      const previous = item.previousElementSibling;
      previous.insertAdjacentHTML('beforeend', bookmark() + body.innerHTML);
      item.remove();
      replace(list, copy.outerHTML);
    } else {
      item.remove();
      const tag = body.querySelector('ol, ul') ? 'div' : 'p';
      replace(list, `<${tag}>${bookmark()}${body.innerHTML || '<br>'}</${tag}>` +
        (copy.children.length ? copy.outerHTML : ''));
    }
  }
  function keydown(event) {
    if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey && event.keyCode !== 229) enter(event);
    if (event.key === 'Backspace' && event.keyCode !== 229) backward(event);
    if (event.key === 'Tab' && closest(win.getSelection().anchorNode, 'li') && editable()) {
      event.preventDefault();
      indent(event.shiftKey);
    }
  }
  function input(event) {
    if (busy || composing || event.isComposing) return;
    const li = closest(win.getSelection().anchorNode, 'li');
    normalize(enteringItem && li !== enteringItem ? li : null);
    enteringItem = null;
    onChange();
  }
  function change(event) {
    if (event.target.matches('input[type="checkbox"]')) {
      event.target.toggleAttribute('checked', event.target.checked);
      onChange();
    }
  }
  function click(event) {
    const box = closest(event.target, '.x-todo-box');
    if (!box) return;
    if (!editable()) event.preventDefault();
    else if (event.target === box) box.querySelector('input')?.click();
  }
  const compositionStart = () => { composing = true; };
  const compositionEnd = () => { composing = false; normalize(); onChange(); };
  const toggle = () => onChange();
  const listeners = { beforeinput: beforeInput, keydown, input, change, click,
    compositionstart: compositionStart, compositionend: compositionEnd, toggle };
  Object.entries(listeners).forEach(([name, fn]) => root.addEventListener(name, fn, true));
  doc.addEventListener('selectionchange', remember);
  normalize();
  return { normalize, toggleList, insertDetails, indent, remember, destroy() {
    Object.entries(listeners).forEach(([name, fn]) => root.removeEventListener(name, fn, true));
    doc.removeEventListener('selectionchange', remember);
  }};
}
